import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';

import type { ReduxState } from 'app/redux/types';

import { DictionaryData, type tFunction, useTranslation } from 'app/lang';
import { accountCity, accountEmail, accountVatNumber } from 'app/lib/account/selectors';

import { PersonalItem } from '../../../PersonalItem';
import { AdditionalInfoModal } from './AdditionalInfoModal';

import commonDictionary from './AdditionalInfo.dictionary.json';

import styles from './AdditionalInfo.module.scss';

const DIGITS_REGEXP = /^\d+$/;
const EMAIL_REGEXP = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;

type AdditionalInfoProps = {
  variant: 'city' | 'vatNumber' | 'email';
};

type ValidationErrors = {
  value?: string;
};

type VariantData = {
  selector: (state: ReduxState) => string | undefined;
  modalParamKey: string;
  validator?: (value: ValidationErrors) => ValidationErrors;
  dictionary: {
    label: DictionaryData;
    linkTitle: DictionaryData;
    modalTitle: DictionaryData;
  };
};


const getVariants = (t: tFunction): Record<AdditionalInfoProps['variant'], VariantData> => ({
  vatNumber: {
    selector: accountVatNumber,
    validator: ({ value }) => {
      const errors: ValidationErrors = {};
      if (!value || value.length === 0 || value.length > 9) {
        errors.value = t(commonDictionary.validationEmptyField);
      }
      if (value && value.length !== 9) {
        errors.value = t(commonDictionary.validationVatNumberLength);
      }
      if (value && !DIGITS_REGEXP.test(value)) {
        errors.value = t(commonDictionary.validationVatNumberFormat);
      }

      return errors;
    },
    modalParamKey: 'personal_data_vatNumber',
    dictionary: {
      label: commonDictionary.innLabel,
      linkTitle: commonDictionary.innLinkTitle,
      modalTitle: commonDictionary.innModalTitle,
    },
  },
  city: {
    selector: accountCity,
    validator: ({ value }) => {
      const errors: ValidationErrors = {};
      if (!value || value.length === 0) {
        errors.value = t(commonDictionary.validationEmptyField);
      }

      return errors;
    },
    modalParamKey: 'personal_data_city',
    dictionary: {
      label: commonDictionary.cityLabel,
      linkTitle: commonDictionary.cityLinkTitle,
      modalTitle: commonDictionary.cityModalTitle,
    },
  },
  email: {
    selector: accountEmail,
    validator: ({ value }) => {
      const errors: ValidationErrors = {};
      if (!value || value.length === 0) {
        errors.value = t(commonDictionary.validationEmptyField);
      }
      if (value && !EMAIL_REGEXP.test(value)) {
        errors.value = t(commonDictionary.validationEmail);
      }

      return errors;
    },
    modalParamKey: 'personal_data_email',
    dictionary: {
      label: commonDictionary.emailLabel,
      linkTitle: commonDictionary.emailLinkTitle,
      modalTitle: commonDictionary.emailModalTitle,
    },
  },

});

export const AdditionalInfo = ({ variant }: AdditionalInfoProps) => {
  const { t } = useTranslation();
  const { location, replace } = useHistory();
  const urlParams = new URLSearchParams(location.search);

  const { selector, modalParamKey, dictionary, validator } = getVariants(t)[variant];

  const data = useSelector(selector);
  const isOpen = urlParams.has(modalParamKey);
  const onClose = () => replace({ search: '' });

  const personalItem = (
    <PersonalItem label={t(dictionary.label)}
                  value={data} />
  );

  if (data?.length) {
    return <div className={styles.root}>{personalItem}</div>;
  }

  return (
    <div className={styles.root}>
      {personalItem}
      <Link className={styles.link}
            to={`?${modalParamKey}`}>
        {t(dictionary.linkTitle)}
      </Link>

      <AdditionalInfoModal title={t(dictionary.modalTitle)}
                           showModal={isOpen}
                           handleClose={onClose}
                           variant={variant}
                           selector={selector}
                           validator={validator} />
    </div>
  );
};
